import { useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Loader2,
  Plus,
  Trash2,
  GripVertical,
  Copy,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import { leadIntakeFormsAPI, usersAPI } from '../utils/api';
import {
  LeadIntakeFieldType,
  LEAD_INTAKE_FIELD_TYPES,
  LEAD_INTAKE_FIELD_TYPE_LABEL,
  CAPTCHA_PROVIDER_LABEL,
  IntakeFieldDef,
  LeadIntakeFormCreate,
  LeadIntakeFormUpdate,
} from '../types/leadIntakeForm';
import ConfirmModal from '../components/ConfirmModal';
import { usePermissions } from '../hooks/usePermissions';

const PUBLIC_API_URL =
  (window as any).CONFIG?.VITE_PUBLIC_API_URL ||
  import.meta.env.VITE_PUBLIC_API_URL ||
  'http://localhost:8100/api/v1';

const fieldSchema = z.object({
  name: z
    .string()
    .min(1, 'Obbligatorio')
    .regex(/^[A-Za-z_][A-Za-z0-9_]*$/, 'Solo lettere, numeri e _ (inizio con lettera o _)'),
  label: z.string().min(1, 'Obbligatorio').max(200),
  type: z.enum(['text', 'email', 'phone', 'textarea', 'select', 'checkbox', 'hidden']),
  required: z.boolean(),
  placeholder: z.string().max(200).optional().or(z.literal('')),
  optionsRaw: z.string().optional(), // csv or newline-separated
});

const formSchema = z
  .object({
    slug: z
      .string()
      .min(4, 'Min 4 caratteri')
      .max(120)
      .regex(
        /^[a-z0-9][a-z0-9-]*[a-z0-9]$/,
        'Lowercase alfanumerico + trattino, inizio/fine con lettera/numero'
      ),
    titolo: z.string().min(1, 'Obbligatorio').max(200),
    descrizione: z.string().optional().or(z.literal('')),
    rate_limit_per_hour: z.coerce.number().int().min(1).max(10000),
    captcha_provider: z.enum(['recaptcha_v3', 'hcaptcha']).optional().or(z.literal('')),
    captcha_secret: z.string().max(200).optional().or(z.literal('')),
    redirect_url: z
      .string()
      .max(500)
      .optional()
      .or(z.literal('')),
    is_active: z.boolean(),
    assigned_agente_id: z.string().optional().or(z.literal('')),
    allowed_origins_raw: z.string().optional().or(z.literal('')), // one per line
    fields: z.array(fieldSchema).min(0).max(50),
  })
  .superRefine((data, ctx) => {
    // Coerenza captcha
    if (data.captcha_provider && !data.captcha_secret) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['captcha_secret'],
        message: 'Secret richiesto quando il provider è impostato',
      });
    }
    // Select richiede options
    data.fields.forEach((f, i) => {
      if (f.type === 'select') {
        const opts = (f.optionsRaw || '')
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean);
        if (opts.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['fields', i, 'optionsRaw'],
            message: 'Almeno una opzione richiesta per type=select',
          });
        }
      }
    });
    // Origins validati lato backend; qui solo check sintassi leggera
    const origins = (data.allowed_origins_raw || '')
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    const originRegex = /^https?:\/\/(\*\.)?[A-Za-z0-9][A-Za-z0-9.-]*(:\d+)?$/;
    origins.forEach((o) => {
      if (!originRegex.test(o)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['allowed_origins_raw'],
          message: `Origin non valido: ${o} (es. https://www.acme.com o https://*.acme.com)`,
        });
      }
    });
  });

type FormValues = z.infer<typeof formSchema>;

const emptyField: FormValues['fields'][number] = {
  name: '',
  label: '',
  type: 'text',
  required: false,
  placeholder: '',
  optionsRaw: '',
};

const defaultValues: FormValues = {
  slug: '',
  titolo: '',
  descrizione: '',
  rate_limit_per_hour: 20,
  captcha_provider: '',
  captcha_secret: '',
  redirect_url: '',
  is_active: true,
  assigned_agente_id: '',
  allowed_origins_raw: '',
  fields: [
    { name: 'nome', label: 'Nome', type: 'text', required: true, placeholder: '', optionsRaw: '' },
    {
      name: 'email',
      label: 'Email',
      type: 'email',
      required: true,
      placeholder: '',
      optionsRaw: '',
    },
    {
      name: 'messaggio',
      label: 'Messaggio',
      type: 'textarea',
      required: false,
      placeholder: '',
      optionsRaw: '',
    },
  ],
};

function parseOptions(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw.split('\n').map((s) => s.trim()).filter(Boolean);
}

function mapFormToPayload(values: FormValues): LeadIntakeFormCreate {
  const fields: IntakeFieldDef[] = values.fields.map((f) => ({
    name: f.name,
    label: f.label,
    type: f.type,
    required: f.required,
    placeholder: f.placeholder || null,
    options: f.type === 'select' ? parseOptions(f.optionsRaw) : undefined,
  }));
  const origins = (values.allowed_origins_raw || '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
  return {
    slug: values.slug,
    titolo: values.titolo,
    descrizione: values.descrizione || null,
    fields,
    allowed_origins: origins,
    rate_limit_per_hour: values.rate_limit_per_hour,
    captcha_provider: values.captcha_provider || null,
    captcha_secret: values.captcha_secret || null,
    redirect_url: values.redirect_url || null,
    is_active: values.is_active,
    assigned_agente_id: values.assigned_agente_id || null,
  };
}

export default function LeadIntakeFormEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('leads:config:edit');
  const isEdit = !!id;

  const [secretDirty, setSecretDirty] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const { data: existing, isLoading: isLoadingExisting } = useQuery({
    queryKey: ['lead-intake-form', id],
    queryFn: async () => {
      const res = await leadIntakeFormsAPI.getForm(id!);
      return res.data;
    },
    enabled: isEdit,
  });

  const { data: agents } = useQuery({
    queryKey: ['users-select-active'],
    queryFn: async () => {
      const res = await usersAPI.getUsers({ limit: 100, is_active: true });
      return res.data.data;
    },
  });

  const initialValues: FormValues = useMemo(() => {
    if (!existing) return defaultValues;
    return {
      slug: existing.slug,
      titolo: existing.titolo,
      descrizione: existing.descrizione || '',
      rate_limit_per_hour: existing.rate_limit_per_hour,
      captcha_provider: existing.captcha_provider || '',
      captcha_secret: '', // never shown, write-only
      redirect_url: existing.redirect_url || '',
      is_active: existing.is_active,
      assigned_agente_id: existing.assigned_agente_id || '',
      allowed_origins_raw: (existing.allowed_origins || []).join('\n'),
      fields: (existing.fields || []).map((f) => ({
        name: f.name,
        label: f.label,
        type: f.type,
        required: !!f.required,
        placeholder: f.placeholder || '',
        optionsRaw: (f.options || []).join('\n'),
      })),
    };
  }, [existing]);

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
    values: initialValues,
  });

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'fields',
  });

  const slug = watch('slug');
  const titolo = watch('titolo');
  const watchFields = watch('fields');
  const captchaProvider = watch('captcha_provider');

  const saveMutation = useMutation({
    mutationFn: (values: FormValues) => {
      const payload = mapFormToPayload(values);
      if (isEdit) {
        // In edit mode, omit captcha_secret unless user typed a new one
        const update: LeadIntakeFormUpdate = { ...payload };
        if (!secretDirty) delete update.captcha_secret;
        return leadIntakeFormsAPI.updateForm(id!, update);
      }
      return leadIntakeFormsAPI.createForm(payload);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['lead-intake-forms'] });
      queryClient.invalidateQueries({ queryKey: ['lead-intake-form', res.data.id] });
      toast.success(isEdit ? 'Form aggiornato' : 'Form creato');
      if (!isEdit) navigate(`/leads/intake-forms/${res.data.id}/edit`);
    },
    onError: (error: any) => {
      const status = error.response?.status;
      if (status === 409) {
        toast.error('Slug già in uso, sceglierne un altro');
      } else {
        toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
      }
    },
  });

  const rotateMutation = useMutation({
    mutationFn: () => leadIntakeFormsAPI.rotateSlug(id!),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['lead-intake-form', id] });
      queryClient.invalidateQueries({ queryKey: ['lead-intake-forms'] });
      toast.success(`Slug ruotato: ${res.data.slug}`);
      setConfirmRotate(false);
      // reset form with new slug
      reset({ ...initialValues, slug: res.data.slug });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nella rotazione');
    },
  });

  const embedSnippet = useMemo(() => {
    if (!slug) return '';
    return `<!-- Offertify Lead Intake Form: ${titolo || slug} -->\n<form action="${PUBLIC_API_URL}/public/leads/intake/${slug}" method="POST" enctype="application/json">\n${(watchFields || [])
      .filter((f) => f.type !== 'hidden')
      .map((f) => {
        if (f.type === 'textarea') {
          return `  <label>${f.label}</label>\n  <textarea name="${f.name}" ${f.required ? 'required' : ''}></textarea>`;
        }
        if (f.type === 'select') {
          const opts = parseOptions(f.optionsRaw);
          return `  <label>${f.label}</label>\n  <select name="${f.name}" ${f.required ? 'required' : ''}>\n${opts.map((o) => `    <option value="${o}">${o}</option>`).join('\n')}\n  </select>`;
        }
        if (f.type === 'checkbox') {
          return `  <label><input type="checkbox" name="${f.name}" ${f.required ? 'required' : ''} /> ${f.label}</label>`;
        }
        const htmlType = f.type === 'phone' ? 'tel' : f.type;
        return `  <label>${f.label}</label>\n  <input type="${htmlType}" name="${f.name}" ${f.required ? 'required' : ''} />`;
      })
      .join('\n')}\n  <button type="submit">Invia</button>\n</form>`;
  }, [slug, titolo, watchFields]);

  const copySnippet = () => {
    navigator.clipboard.writeText(embedSnippet).then(
      () => toast.success('Snippet copiato negli appunti'),
      () => toast.error('Impossibile copiare: copia manualmente')
    );
  };

  const onDragStart = (idx: number) => setDraggingIdx(idx);
  const onDragEnd = () => {
    setDraggingIdx(null);
    setDragOverIdx(null);
  };
  const onDropField = (e: React.DragEvent, target: number) => {
    e.preventDefault();
    const source = draggingIdx;
    setDragOverIdx(null);
    setDraggingIdx(null);
    if (source === null || source === target) return;
    move(source, target);
  };

  if (isEdit && isLoadingExisting) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const errorClass = 'text-xs text-red-500 mt-0.5';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/leads/intake-forms')}
            className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            Form pubblici
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Modifica form' : 'Nuovo form pubblico'}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {isEdit && canEdit && (
            <button
              onClick={() => setConfirmRotate(true)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50"
            >
              <RefreshCw className="h-4 w-4" />
              Ruota slug
            </button>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit((v) => saveMutation.mutate(v))} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT: editing */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic info */}
            <div className="bg-white rounded-lg shadow p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Dati principali</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Titolo *</label>
                  <input type="text" {...register('titolo')} className={inputClass} />
                  {errors.titolo && <p className={errorClass}>{errors.titolo.message}</p>}
                </div>
                <div>
                  <label className={labelClass}>Slug * (nell&rsquo;URL pubblico)</label>
                  <input
                    type="text"
                    {...register('slug')}
                    className={inputClass}
                    placeholder="acme-contattaci"
                    style={{ textTransform: 'lowercase' }}
                  />
                  {errors.slug && <p className={errorClass}>{errors.slug.message}</p>}
                </div>
              </div>
              <div>
                <label className={labelClass}>Descrizione</label>
                <textarea {...register('descrizione')} rows={2} className={inputClass} />
              </div>
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    {...register('is_active')}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  Form attivo
                </label>
              </div>
            </div>

            {/* CORS + rate limit + agent */}
            <div className="bg-white rounded-lg shadow p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Sicurezza e assegnazione</h3>
              <div>
                <label className={labelClass}>
                  Origini consentite (una per riga, wildcard <code>*.</code> ammessa)
                </label>
                <textarea
                  {...register('allowed_origins_raw')}
                  rows={4}
                  className={`${inputClass} font-mono text-xs`}
                  placeholder={`https://www.acme.com\nhttps://*.acme.com`}
                />
                {errors.allowed_origins_raw && (
                  <p className={errorClass}>{errors.allowed_origins_raw.message}</p>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>Rate limit per ora</label>
                  <input
                    type="number"
                    min={1}
                    max={10000}
                    {...register('rate_limit_per_hour', { valueAsNumber: true })}
                    className={inputClass}
                  />
                  {errors.rate_limit_per_hour && (
                    <p className={errorClass}>{errors.rate_limit_per_hour.message}</p>
                  )}
                </div>
                <div>
                  <label className={labelClass}>Agente assegnato</label>
                  <select {...register('assigned_agente_id')} className={inputClass}>
                    <option value="">Nessuno</option>
                    {agents?.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name || u.email}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Redirect URL post-submit</label>
                  <input
                    type="url"
                    {...register('redirect_url')}
                    className={inputClass}
                    placeholder="https://..."
                  />
                </div>
              </div>
            </div>

            {/* Captcha */}
            <div className="bg-white rounded-lg shadow p-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Captcha (opzionale)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Provider</label>
                  <select {...register('captcha_provider')} className={inputClass}>
                    <option value="">Nessuno</option>
                    {(Object.keys(CAPTCHA_PROVIDER_LABEL) as Array<keyof typeof CAPTCHA_PROVIDER_LABEL>).map(
                      (p) => (
                        <option key={p} value={p}>
                          {CAPTCHA_PROVIDER_LABEL[p]}
                        </option>
                      )
                    )}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>
                    Secret {isEdit && existing?.has_captcha_secret && !secretDirty ? '(configurato)' : ''}
                  </label>
                  <input
                    type="password"
                    autoComplete="new-password"
                    {...register('captcha_secret', {
                      onChange: () => setSecretDirty(true),
                    })}
                    className={inputClass}
                    placeholder={
                      isEdit && existing?.has_captcha_secret
                        ? '•••••••• (lascia vuoto per non modificare)'
                        : captchaProvider
                          ? 'Secret del provider'
                          : ''
                    }
                    disabled={!captchaProvider}
                  />
                  {errors.captcha_secret && (
                    <p className={errorClass}>{errors.captcha_secret.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Fields editor */}
            <div className="bg-white rounded-lg shadow p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Campi del form</h3>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => append(emptyField)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4" />
                    Aggiungi campo
                  </button>
                )}
              </div>
              {fields.length === 0 ? (
                <div className="text-center text-sm text-gray-500 py-6">
                  Nessun campo. Clicca &ldquo;Aggiungi campo&rdquo;.
                </div>
              ) : (
                <ul className="space-y-3">
                  {fields.map((f, idx) => {
                    const fieldType = watchFields?.[idx]?.type as LeadIntakeFieldType;
                    return (
                      <li
                        key={f.id}
                        draggable={canEdit}
                        onDragStart={(e) => {
                          if (!canEdit) return;
                          e.dataTransfer.effectAllowed = 'move';
                          onDragStart(idx);
                        }}
                        onDragEnd={onDragEnd}
                        onDragOver={(e) => {
                          if (!canEdit) return;
                          e.preventDefault();
                          setDragOverIdx(idx);
                        }}
                        onDragLeave={() => setDragOverIdx((v) => (v === idx ? null : v))}
                        onDrop={(e) => canEdit && onDropField(e, idx)}
                        className={`border rounded-lg p-3 ${
                          dragOverIdx === idx ? 'border-blue-400 bg-blue-50/40' : 'border-gray-200'
                        } ${draggingIdx === idx ? 'opacity-50' : ''}`}
                      >
                        <div className="flex items-start gap-2">
                          <span
                            className="mt-2 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
                            title="Trascina per riordinare"
                          >
                            <GripVertical className="h-5 w-5" />
                          </span>
                          <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-2">
                            <div className="md:col-span-3">
                              <label className="block text-xs text-gray-500 mb-0.5">Name</label>
                              <input
                                type="text"
                                {...register(`fields.${idx}.name` as const)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm font-mono"
                              />
                              {errors.fields?.[idx]?.name && (
                                <p className={errorClass}>{errors.fields[idx]?.name?.message}</p>
                              )}
                            </div>
                            <div className="md:col-span-4">
                              <label className="block text-xs text-gray-500 mb-0.5">Label</label>
                              <input
                                type="text"
                                {...register(`fields.${idx}.label` as const)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                              {errors.fields?.[idx]?.label && (
                                <p className={errorClass}>{errors.fields[idx]?.label?.message}</p>
                              )}
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-xs text-gray-500 mb-0.5">Tipo</label>
                              <select
                                {...register(`fields.${idx}.type` as const)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              >
                                {LEAD_INTAKE_FIELD_TYPES.map((t) => (
                                  <option key={t} value={t}>
                                    {LEAD_INTAKE_FIELD_TYPE_LABEL[t]}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-xs text-gray-500 mb-0.5">Placeholder</label>
                              <input
                                type="text"
                                {...register(`fields.${idx}.placeholder` as const)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            </div>
                            <div className="md:col-span-1 flex items-end gap-2">
                              <Controller
                                name={`fields.${idx}.required` as const}
                                control={control}
                                render={({ field }) => (
                                  <label className="inline-flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={!!field.value}
                                      onChange={(e) => field.onChange(e.target.checked)}
                                      className="h-4 w-4 rounded border-gray-300"
                                    />
                                    Req
                                  </label>
                                )}
                              />
                            </div>
                            {fieldType === 'select' && (
                              <div className="md:col-span-12">
                                <label className="block text-xs text-gray-500 mb-0.5">
                                  Opzioni (una per riga)
                                </label>
                                <textarea
                                  {...register(`fields.${idx}.optionsRaw` as const)}
                                  rows={3}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm font-mono"
                                  placeholder={'opzione1\nopzione2'}
                                />
                                {errors.fields?.[idx]?.optionsRaw && (
                                  <p className={errorClass}>
                                    {errors.fields[idx]?.optionsRaw?.message}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                          {canEdit && (
                            <button
                              type="button"
                              onClick={() => remove(idx)}
                              className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                              title="Rimuovi campo"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate('/leads/intake-forms')}
                className="px-4 py-2 border border-gray-300 bg-white rounded-lg hover:bg-gray-50 text-sm"
              >
                Annulla
              </button>
              {canEdit && (
                <button
                  type="submit"
                  disabled={saveMutation.isPending || (isEdit && !isDirty && !secretDirty)}
                  className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
                >
                  {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isEdit ? 'Salva modifiche' : 'Crea form'}
                </button>
              )}
            </div>
          </div>

          {/* RIGHT: preview + snippet */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                Anteprima
              </h3>
              <PreviewForm fields={watchFields || []} titolo={titolo} />
            </div>

            <div className="bg-white rounded-lg shadow p-6 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  Snippet embed
                </h3>
                <button
                  type="button"
                  onClick={copySnippet}
                  disabled={!slug}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copia
                </button>
              </div>
              {slug ? (
                <textarea
                  readOnly
                  value={embedSnippet}
                  rows={10}
                  className="w-full px-2 py-1 border border-gray-200 bg-gray-50 rounded text-xs font-mono"
                />
              ) : (
                <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>Imposta uno slug valido per generare lo snippet.</span>
                </div>
              )}
              <p className="text-xs text-gray-500">
                URL pubblico: <code>{`${PUBLIC_API_URL}/public/leads/intake/${slug || '{slug}'}`}</code>
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6 space-y-2 text-xs text-gray-600">
              <p className="font-semibold text-gray-900">Note</p>
              <p>
                Dopo la pubblicazione, i submit generano un <code>Lead</code> con{' '}
                <code>source=web_form</code> e una <code>LeadActivity type=form_submit</code> con
                l&rsquo;intero payload in <code>metadata</code>.
              </p>
              <p>
                La rotazione slug invalida le statistiche storiche. Il conteggio ultimi 30gg si
                azzera.
              </p>
              {isEdit && (
                <p>
                  <Link
                    to={`/leads?source=web_form&search=${encodeURIComponent(existing?.slug || '')}`}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Vai ai lead generati da questo form →
                  </Link>
                </p>
              )}
            </div>
          </div>
        </div>
      </form>

      {confirmRotate && (
        <ConfirmModal
          isOpen={confirmRotate}
          title="Ruota slug"
          message={
            `Verrà generato un nuovo slug random per questo form. Lo slug vecchio smetterà ` +
            `immediatamente di funzionare: tutti gli embed in giro andranno aggiornati. ` +
            `Inoltre le statistiche storiche legate allo slug vecchio saranno invalidate. Procedere?`
          }
          confirmLabel="Ruota"
          onConfirm={() => rotateMutation.mutate()}
          onClose={() => setConfirmRotate(false)}
          isLoading={rotateMutation.isPending}
          variant="warning"
        />
      )}
    </div>
  );
}

function PreviewForm({
  fields,
  titolo,
}: {
  fields: FormValues['fields'];
  titolo: string;
}) {
  if (!fields.length) {
    return (
      <p className="text-sm text-gray-400 italic">
        Aggiungi campi per vedere l&rsquo;anteprima.
      </p>
    );
  }
  return (
    <div className="border border-gray-200 rounded p-4 bg-gray-50">
      {titolo && <h4 className="text-base font-semibold mb-3 text-gray-900">{titolo}</h4>}
      <div className="space-y-3">
        {fields.map((f, i) => {
          if (f.type === 'hidden') {
            return (
              <div key={i} className="text-xs text-gray-400 italic">
                [hidden] {f.name}
              </div>
            );
          }
          const label = (
            <label className="block text-xs font-medium text-gray-700 mb-0.5">
              {f.label}
              {f.required && <span className="text-red-500"> *</span>}
            </label>
          );
          if (f.type === 'textarea') {
            return (
              <div key={i}>
                {label}
                <textarea
                  disabled
                  rows={3}
                  placeholder={f.placeholder || ''}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-white"
                />
              </div>
            );
          }
          if (f.type === 'select') {
            const opts = parseOptions(f.optionsRaw);
            return (
              <div key={i}>
                {label}
                <select disabled className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-white">
                  {opts.length ? (
                    opts.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))
                  ) : (
                    <option>— nessuna opzione —</option>
                  )}
                </select>
              </div>
            );
          }
          if (f.type === 'checkbox') {
            return (
              <label key={i} className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" disabled className="h-4 w-4 rounded border-gray-300" />
                {f.label}
              </label>
            );
          }
          const htmlType = f.type === 'phone' ? 'tel' : f.type;
          return (
            <div key={i}>
              {label}
              <input
                disabled
                type={htmlType}
                placeholder={f.placeholder || ''}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-white"
              />
            </div>
          );
        })}
        <button
          disabled
          type="button"
          className="mt-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded opacity-60 cursor-not-allowed"
        >
          Invia
        </button>
      </div>
    </div>
  );
}
